// src/layout/Layout.jsx
import React, { useContext, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header.jsx';
import Footer from '../components/Footer.jsx';
import UsageBanner from '../components/UsageBanner.jsx';
import { AuthContext } from '../context/AuthContext';
import config from '../config';

const Layout = ({ children }) => {
  const { user, resetUploadUsage, uploadUsage } = useContext(AuthContext);
  const [searchParams, setSearchParams] = useSearchParams();
  const [paymentMessage, setPaymentMessage] = useState(null); // 'success' | 'canceled' | null
  const [paymentRefreshing, setPaymentRefreshing] = useState(false); // true while refetching usage after payment
  const retryTimeoutsRef = useRef([]);
  const initialDelayRef = useRef(null);

  useEffect(() => {
    const payment = searchParams.get('payment');
    if (payment !== 'success' && payment !== 'canceled') return;

    const email = user?.email;
    setPaymentMessage(payment);

    const clearPaymentParam = () => {
      const next = new URLSearchParams(searchParams);
      next.delete('payment');
      next.delete('session_id');
      setSearchParams(next, { replace: true });
    };

    if (payment === 'canceled') {
      clearPaymentParam();
      return;
    }

    if (payment === 'success') {
      if (!resetUploadUsage || !email) {
        return;
      }
      const sessionId = searchParams.get('session_id');

      const refetchUsage = () => resetUploadUsage(email, { bustCache: true });

      if (sessionId) {
        // Frontend-driven confirmation: backend updates lines, then we refetch with cache-bust so banner shows new lines
        axios.post(`${config.apiBaseUrl}/api/stripe/confirm-payment`, { sessionId })
          .then(() => {
            refetchUsage().then(() => {
              // Second refetch after short delay so UI reliably shows updated lines
              const delayedRefetch = setTimeout(() => {
                refetchUsage().then(() => {
                  clearPaymentParam();
                  retryTimeoutsRef.current = [
                    setTimeout(() => refetchUsage(), 2000),
                    setTimeout(() => refetchUsage(), 4500)
                  ];
                });
              }, 400);
              retryTimeoutsRef.current = [delayedRefetch];
            });
          })
          .catch((err) => {
            console.error('Confirm payment failed:', err);
            refetchUsage().then(() => clearPaymentParam());
          });
      } else {
        // No session_id (e.g. old link): refetch with cache-bust after a short delay
        initialDelayRef.current = setTimeout(() => {
          initialDelayRef.current = null;
          refetchUsage().then(() => {
            clearPaymentParam();
            retryTimeoutsRef.current = [
              setTimeout(() => refetchUsage(), 2000),
              setTimeout(() => refetchUsage(), 4500)
            ];
          });
        }, 1500);
      }
    }

    return () => {
      if (initialDelayRef.current) {
        clearTimeout(initialDelayRef.current);
        initialDelayRef.current = null;
      }
    };
  }, [searchParams, setSearchParams, resetUploadUsage, user?.email]);

  useEffect(() => {
    return () => {
      retryTimeoutsRef.current.forEach((id) => clearTimeout(id));
      retryTimeoutsRef.current = [];
    };
  }, []);

  const dismissPaymentMessage = () => setPaymentMessage(null);

  return (
    <div className="layout-container">
      {paymentMessage === 'success' && (
        <div className="payment-success-banner" role="alert">
          <span>
            {paymentRefreshing
              ? 'Payment successful. Updating your line balance…'
              : 'Payment successful. Your new line balance has been updated.'}
          </span>
          <button type="button" onClick={dismissPaymentMessage} aria-label="Dismiss">×</button>
        </div>
      )}
      {paymentMessage === 'canceled' && (
        <div className="payment-canceled-banner" role="alert">
          <span>Payment was canceled. No charges were made.</span>
          <button type="button" onClick={dismissPaymentMessage} aria-label="Dismiss">×</button>
        </div>
      )}
      <Header />
      {user?.email && <UsageBanner />}
      <main className="main-content">{children}</main>
      <Footer />
    </div>
  );
};

export default Layout;
